import React from "react";
import {
  HiFire,
  HiHeart,
  HiDocumentText,
  HiSparkles,
  HiCube,
  HiOutlineUser,
  HiShieldCheck,
} from "react-icons/hi";

import icon from "../../assets/peak-icon.svg";

import { HiTrophy, HiXMark } from "react-icons/hi2";
import { UserPublicData } from "../../types/user";
import ComingSoon from "../ComingSoon";

export type LogType =
  | "training"
  | "wellness"
  | "note"
  | "performance"
  | "recovery"
  | "negativeInfluences"
  | "niggle"
  | "health"
  | "step";

interface AddLogModalProps {
  open: boolean;
  onClose: () => void;
  onSelectLogType: (logType: LogType) => void;
  dateID: string;
  existingWellnessLogs?: any[]; // Array of existing wellness logs for this date
  existingHealthLogs?: any[]; // Array of existing health logs for this date
  userData?: UserPublicData | null; // User data to check for casual mode
}

interface LogTypeOption {
  type: LogType;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const logTypes: LogTypeOption[] = [
  {
    type: "training",
    title: "Training",
    description: "Record your workout intensity and notes",
    icon: <HiFire />,
    color: "oklch(var(--bc))",
  },
  {
    type: "recovery",
    title: "Recovery",
    description: "Document recovery activities",
    icon: <HiSparkles />,
    color: "oklch(var(--bc))",
  },
  {
    type: "niggle",
    title: "Niggle",
    description: "Track pain and injury concerns",
    icon: <HiShieldCheck />,
    color: "oklch(var(--bc))",
  },
  {
    type: "performance",
    title: "Performance",
    description: "Game day, competition or testing",
    icon: <HiTrophy />,
    color: "oklch(var(--bc))",
  },
  {
    type: "health",
    title: "Health Stats",
    description: "Record health metrics like HRV and RHR",
    icon: <HiCube />,
    color: "oklch(var(--bc))",
  },
  // {
  //   type: "step",
  //   title: "Steps",
  //   description: "Log daily step count",
  //   icon: <HiOutlineUser />,
  //   color: "#303030",
  // },
  {
    type: "note",
    title: "Note",
    description: "Add a note to your day",
    icon: <HiDocumentText />,
    color: "oklch(var(--bc))",
  },
  {
    type: "wellness",
    title: "Wellness",
    description: "Track your overall wellness and sleep",
    icon: <HiHeart />,
    color: "oklch(var(--bc))",
  },
];

const AddLogModal: React.FC<AddLogModalProps> = ({
  open,
  onClose,
  onSelectLogType,
  dateID,
  existingWellnessLogs = [],
  existingHealthLogs = [],
  userData = null,
}) => {
  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSelectLogType = (logType: LogType) => {
    console.log("AddLogModal: Selected log type:", logType);
    onSelectLogType(logType);
    // Don't call onClose() here - let the parent handle it
  };

  // Filter out wellness and health logs based on conditions
  const availableLogTypes = logTypes.filter((logType) => {
    // Hide wellness and health logs for casual mode users
    if (userData?.casualMode) {
      if (logType.type === "wellness" || logType.type === "health") {
        return false;
      }
    }

    // Filter out wellness and health logs if they already exist for this day
    if (logType.type === "wellness" && existingWellnessLogs.length > 0) {
      return false; // Don't show wellness option if there's already a wellness log
    }
    if (logType.type === "health" && existingHealthLogs.length > 0) {
      return false; // Don't show health option if there's already a health log
    }
    return true;
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-10 bg-gradient-to-b from-white from-0% via-gray-400/40 via-50% to-gray-400/40 backdrop-blur"
      style={{
        WebkitTransform: "translateZ(0)",
        transform: "translateZ(0)",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
      }}
    >
      <div className="bg-transparent min-w-[280px] max-w-md w-full ">
        <div className="flex justify-end pr-4">
          <button
            className="p-1 h-10 w-10 bg-black bg-opacity-10 rounded-full text-gray-800"
            onClick={onClose}
          >
            <HiXMark className="text-xl h-8 w-8" />
          </button>
        </div>
        {/* Header */}

        <div className="px-4 pt-4 pb-10">
          <h3 className="text-5xl text-gray-800 font-groteskLogo">^</h3>
          <h2 className="text-md font-light text-gray-600 text-left font-outfit">
            {formatDate(dateID)}
          </h2>
          <h1 className="text-gray-800 text-4xl font-bold">Log</h1>
        </div>

        {/* Log Type Options */}
        <div className="py-2 px-2" role="menu" aria-label="Log type options">
          {availableLogTypes.map((logType, index) => (
            <div key={logType.type} className="mb-3">
              <button
                onClick={() => handleSelectLogType(logType.type)}
                role="menuitem"
                aria-label={`Add ${logType.title} log: ${logType.description}`}
                className="w-full py-3 px-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <div className="flex items-center justify-between">
                  <span className="font-grotesk font-bold text-xl text-gray-800">
                    {logType.title}
                  </span>
                  {/* <div className="w-6 h-7 text-gray-600 rounded-xl flex items-center justify-center">
                    <div className="w-6 h-6">{logType.icon}</div>
                  </div> */}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddLogModal;
